import { useState } from "react";
import { X, Building2, Copy, Check, FileText, ArrowRight, Banknote, Info, Printer, Link2 } from "lucide-react";

const TABS = [
  { key: "bank",   label: "Link a bank",   icon: Link2 },
  { key: "ra",     label: "Routing + Account", icon: Banknote },
  { key: "dd",     label: "Direct Deposit", icon: FileText },
];

export default function AddMoneyModal({ open, onClose, banking, userName }) {
  const [tab, setTab] = useState("bank");
  const [copied, setCopied] = useState("");

  if (!open) return null;
  const spending = banking?.accounts?.spending;
  const routing = spending?.routing_number || "";
  const account = spending?.account_number || "";

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* noop */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="add-money-modal">
      <div className="bg-cream rounded-2xl border border-sand max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-7 pt-7 pb-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">
              Spending account
            </div>
            <h3 className="font-serif text-2xl mt-1">Add money</h3>
          </div>
          <button onClick={onClose} className="text-sage hover:text-ink"
                  data-testid="add-money-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="px-7 flex gap-2 border-b border-sand">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`add-money-tab-${t.key}`}
                className={`pb-3 -mb-px text-xs font-medium flex items-center gap-1.5 border-b-2 transition-colors ${
                  active ? "border-ink text-ink" : "border-transparent text-sage hover:text-ink"
                }`}>
                <Icon className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>

        <div className="px-7 py-5">
          {tab === "bank" && <LinkBankTab />}
          {tab === "ra" && (
            <RoutingAccountTab
              routing={routing} account={account}
              copy={copy} copied={copied}
              userName={userName || ""}
            />
          )}
          {tab === "dd" && (
            <DirectDepositTab
              routing={routing} account={account}
              userName={userName || ""}
              copy={copy} copied={copied}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function LinkBankTab() {
  return (
    <div className="space-y-4" data-testid="add-money-bank-tab">
      <div className="bg-cream-warm border border-sand-warm rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gold-dark/10 flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5 text-gold-dark" strokeWidth={1.5} />
          </div>
          <div>
            <h4 className="font-serif text-lg">Link your external bank</h4>
            <p className="text-xs text-moss mt-1 leading-relaxed">
              Connect Chase, Bank of America, Wells Fargo or 12,000+ other US banks via Plaid.
              Money moves <strong>directly into your Withdraw Guard Spending</strong> — no card, no merchant, no fees on your end.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-linen border border-dashed border-sand rounded-xl p-5 text-center"
           data-testid="add-money-plaid-coming-soon">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-ink/5 mb-2">
          <Link2 className="w-5 h-5 text-sage" strokeWidth={1.5} />
        </div>
        <p className="text-sm font-medium text-ink">Coming soon</p>
        <p className="text-xs text-sage mt-1 max-w-xs mx-auto leading-relaxed">
          Plaid integration is being configured. In the meantime, use <strong>Routing + Account</strong> to
          push money from your bank&apos;s website.
        </p>
      </div>
    </div>
  );
}

function RoutingAccountTab({ routing, account, copy, copied, userName }) {
  if (!routing || !account) {
    return (
      <div className="text-sm text-sage" data-testid="add-money-ra-empty">
        Your account numbers are still being provisioned. Please reload in a few seconds.
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="add-money-ra-tab">
      <div className="bg-cream-warm border border-sand-warm rounded-xl p-4 flex items-start gap-2">
        <Info className="w-4 h-4 text-gold-dark flex-shrink-0 mt-0.5" />
        <p className="text-xs text-moss leading-relaxed">
          Use these numbers in your other bank&apos;s app to push money to Withdraw Guard.
          Funds arrive in <strong>1–3 business days</strong> via ACH, or same-day via wire.
        </p>
      </div>

      <NumberRow label="Account holder" value={userName || "Your name as on file"} copy={copy} copied={copied} field="name" mono={false} />
      <NumberRow label="Routing number (ACH / wire)" value={routing} copy={copy} copied={copied} field="routing" />
      <NumberRow label="Account number" value={account} copy={copy} copied={copied} field="account" />
      <NumberRow label="Account type" value="Checking" copy={copy} copied={copied} field="type" mono={false} />
      <NumberRow label="Bank name" value="Withdraw Guard" copy={copy} copied={copied} field="bank" mono={false} />

      <div className="bg-ink text-cream rounded-xl p-4 text-xs leading-relaxed">
        <div className="font-medium mb-1">How to push money from your other bank</div>
        <ol className="list-decimal list-inside text-sage-mid space-y-1">
          <li>Log into your other bank&apos;s app (Chase / BoA / etc.)</li>
          <li>Tap &quot;Send money&quot; or &quot;External transfer&quot; → &quot;To an account I own&quot;</li>
          <li>Enter the routing + account number above</li>
          <li>Send any amount — it lands in your Spending in 1–3 days</li>
        </ol>
      </div>
    </div>
  );
}

function DirectDepositTab({ routing, account, userName, copy, copied }) {
  if (!routing || !account) {
    return <div className="text-sm text-sage">Account numbers loading…</div>;
  }
  const handlePrint = () => window.print();
  return (
    <div className="space-y-5" data-testid="add-money-dd-tab">
      <div className="bg-cream-warm border border-sand-warm rounded-xl p-4 flex items-start gap-2">
        <Info className="w-4 h-4 text-gold-dark flex-shrink-0 mt-0.5" />
        <p className="text-xs text-moss leading-relaxed">
          Get your paycheck deposited straight into Withdraw Guard Spending — usually <strong>1–2 days earlier</strong>{" "}
          than a regular bank, with no fees. Give the info below to your employer&apos;s HR or payroll system.
        </p>
      </div>

      <div className="border border-sand rounded-xl overflow-hidden bg-white" id="dd-printable">
        <div className="bg-ink text-cream px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Direct Deposit Authorization</div>
            <div className="font-serif text-lg">Withdraw Guard · Spending</div>
          </div>
          <button onClick={handlePrint}
                  className="text-[10px] uppercase tracking-[0.2em] font-semibold text-cream hover:text-white bg-white/10 px-2.5 py-1 rounded inline-flex items-center gap-1"
                  data-testid="add-money-dd-print">
            <Printer className="w-3 h-3" /> Print
          </button>
        </div>
        <div className="p-5 text-sm space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Cell label="Employee" value={userName || "—"} />
            <Cell label="Bank name" value="Withdraw Guard" />
            <Cell label="Account type" value="Checking" />
            <Cell label="Deposit percentage" value="100%" />
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-sand-warm">
            <Cell label="Routing number" mono value={routing} />
            <Cell label="Account number" mono value={account} />
          </div>
          <p className="text-[10px] text-sage pt-2 leading-relaxed">
            I authorize my employer to deposit the net amount of my pay automatically into the account listed above.
            This authorization will remain in effect until I revoke it in writing.
          </p>
        </div>
      </div>

      <button onClick={() => copy("all", `Bank: Withdraw Guard\nAccount type: Checking\nRouting: ${routing}\nAccount: ${account}`)}
              className="w-full bg-forest text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-forest-deep inline-flex items-center justify-center gap-2"
              data-testid="add-money-dd-copy-all">
        {copied === "all" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        {copied === "all" ? "Copied to clipboard" : "Copy all details"}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function NumberRow({ label, value, copy, copied, field, mono = true }) {
  return (
    <div data-testid={`add-money-row-${field}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">{label}</div>
      <div className="mt-1 flex items-center justify-between gap-3 bg-white border border-sand rounded-lg px-4 py-2.5">
        <span className={`text-sm text-ink truncate ${mono ? "font-mono tracking-wider" : ""}`}>
          {value}
        </span>
        <button onClick={() => copy(field, value)}
                className="text-[10px] uppercase tracking-[0.18em] font-semibold text-sage hover:text-ink inline-flex items-center gap-1 shrink-0"
                data-testid={`add-money-copy-${field}-btn`}>
          {copied === field ? <Check className="w-3 h-3 text-forest" /> : <Copy className="w-3 h-3" />}
          {copied === field ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

function Cell({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">{label}</div>
      <div className={`mt-0.5 text-sm text-ink ${mono ? "font-mono tracking-wider" : ""}`}>{value}</div>
    </div>
  );
}
